import { EnvironmentOutlined, ReloadOutlined } from '@ant-design/icons';
import type { DeliveryRow, TrackRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Card, Col, Empty, Input, Row, Segmented, Space, Spin, Table, Tag, Timeline, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../api/client';
import { sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

const DELIVERY_STATUS: Record<string, { text: string; color: string }> = {
  NEW: { text: '待发货', color: 'gold' },
  PICKING: { text: '拣货中', color: 'gold' },
  SHIPPED: { text: '已发货', color: 'blue' },
  ARRIVED: { text: '已到达', color: 'cyan' },
  SIGNED: { text: '已签收', color: 'green' },
  PART_SIGNED: { text: '部分签收', color: 'orange' },
  CANCELED: { text: '已取消', color: 'default' }
};

/** 轨迹节点 → 时间轴颜色（EXCEPTION 用红，SIGN 用绿）。 */
const TRACK_COLOR: Record<string, string> = {
  CREATE: 'gray',
  PICK: 'blue',
  PACK: 'blue',
  SHIP: 'blue',
  ARRIVE: 'cyan',
  SIGN: 'green',
  EXCEPTION: 'red'
};

const TRACK_LABEL: Record<string, string> = {
  CREATE: '建单',
  PICK: '拣货',
  PACK: '打包',
  SHIP: '发车',
  ARRIVE: '到达',
  SIGN: '签收',
  EXCEPTION: '异常'
};

type StatusFilter = 'ALL' | 'SHIPPED' | 'SIGNED' | 'PART_SIGNED';

/** 配送轨迹跟踪：左侧选单、右侧节点时间轴（数据源 `wl_track`，PDA/PC 回传）。 */
export default function DeliveryTrack() {
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [keyword, setKeyword] = useState('');
  const [current, setCurrent] = useState<DeliveryRow | null>(null);

  const deliveryQuery = useQuery({
    queryKey: ['delivery', 'track-picker', status],
    placeholderData: keepPreviousData,
    queryFn: () =>
      hq.delivery.list({
        size: PAGE_SIZE,
        deliveryStatus: status === 'ALL' ? undefined : status
      })
  });

  const deliveries = (deliveryQuery.data?.items ?? []).filter((row) =>
    keyword.trim() === '' ? true : row.bizNo.includes(keyword.trim())
  );

  const trackQuery = useQuery({
    queryKey: ['track', 'timeline', current?.id],
    placeholderData: keepPreviousData,
    queryFn: () => hq.track.list({ deliveryId: current?.id, size: 100 }),
    enabled: Boolean(current?.id)
  });

  const tracks: TrackRow[] = trackQuery.data?.items ?? [];

  const columns: ColumnsType<DeliveryRow> = [
    {
      title: '配送单号',
      dataIndex: 'bizNo',
      width: 190,
      render: (value: string, row) => (
        <Typography.Link onClick={() => setCurrent(row)} strong={row.id === current?.id}>
          {value}
        </Typography.Link>
      )
    },
    {
      title: '状态',
      dataIndex: 'deliveryStatus',
      width: 100,
      render: (value: string) => {
        const meta = DELIVERY_STATUS[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '收货科室', dataIndex: 'toDeptName', width: 120, render: (value: string | null) => value || '-' }
  ];

  return (
    <Row gutter={16}>
      <Col xs={24} xl={10}>
        <Card
          className="page-card"
          title="配送单"
          extra={
            <Space>
              <Input.Search
                allowClear
                placeholder="单号过滤"
                style={{ width: 150 }}
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
              />
              <ReloadOutlined
                style={{ cursor: 'pointer' }}
                onClick={() => deliveryQuery.refetch()}
                spin={deliveryQuery.isFetching}
              />
            </Space>
          }
        >
          <Segmented<StatusFilter>
            value={status}
            options={[
              { label: '全部', value: 'ALL' },
              { label: '在途', value: 'SHIPPED' },
              { label: '已签收', value: 'SIGNED' },
              { label: '部分签收', value: 'PART_SIGNED' }
            ]}
            onChange={(value) => setStatus(value)}
            style={{ marginBottom: 12 }}
          />

          {deliveryQuery.isError ? (
            <Alert
              type="error"
              showIcon
              message="配送单加载失败"
              description={
                deliveryQuery.error instanceof ApiError
                  ? deliveryQuery.error.friendlyMessage
                  : String(deliveryQuery.error)
              }
            />
          ) : null}

          <Table<DeliveryRow>
            rowKey="id"
            size="small"
            columns={columns}
            dataSource={deliveries}
            loading={deliveryQuery.isPending}
            pagination={false}
            scroll={{ x: sumWidth(columns), y: 520 }}
            locale={{ emptyText: '暂无配送单' }}
            onRow={(row) => ({ onClick: () => setCurrent(row), style: { cursor: 'pointer' } })}
          />
        </Card>
      </Col>

      <Col xs={24} xl={14}>
        <Card
          className="page-card"
          title={
            <Space>
              轨迹时间轴
              {current ? <Tag color="blue">{current.bizNo}</Tag> : null}
            </Space>
          }
          extra={
            <Typography.Text type="secondary">
              {current ? `计划 ${formatDisplayTime(current.planTime)} / 发车 ${formatDisplayTime(current.shipAt)}` : '-'}
            </Typography.Text>
          }
        >
          {!current ? (
            <Empty description="请在左侧选择一张配送单" />
          ) : trackQuery.isLoading ? (
            <Spin />
          ) : trackQuery.isError ? (
            <Alert
              type="error"
              showIcon
              message="轨迹加载失败"
              description={
                trackQuery.error instanceof ApiError ? trackQuery.error.friendlyMessage : String(trackQuery.error)
              }
            />
          ) : tracks.length === 0 ? (
            <Empty description="该配送单尚无回传节点（PDA/司机端回传后可见）" />
          ) : (
            <Timeline
              items={tracks.map((row) => ({
                color: TRACK_COLOR[row.trackType] ?? 'blue',
                children: (
                  <Space direction="vertical" size={2}>
                    <Space size={8}>
                      <Tag color={TRACK_COLOR[row.trackType] ?? 'blue'}>
                        {TRACK_LABEL[row.trackType] ?? row.trackType}
                      </Tag>
                      <Typography.Text strong>{formatDisplayTime(row.occurredAt)}</Typography.Text>
                      {row.terminal ? <Tag>{row.terminal}</Tag> : null}
                    </Space>
                    <Typography.Text>{row.trackDesc || '-'}</Typography.Text>
                    {row.location ? (
                      <Typography.Text type="secondary">
                        <EnvironmentOutlined /> {row.location}
                      </Typography.Text>
                    ) : null}
                    {row.deviceNo ? (
                      <Typography.Text type="secondary">设备 {row.deviceNo}</Typography.Text>
                    ) : null}
                  </Space>
                )
              }))}
            />
          )}
        </Card>
      </Col>
    </Row>
  );
}
